// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import throwErrorIfTesting from "@insite/client-framework/Common/ThrowErrorIfTesting";
import {
    ApiHandlerDiscreteParameter,
    createHandlerChainRunnerOptionalParameter,
} from "@insite/client-framework/HandlerCreator";
import { API_URL_CURRENT_FRAGMENT } from "@insite/client-framework/Services/ApiService";
import { addLineCollection } from "@insite/client-framework/Services/CartService";
import loadCurrentCart from "@insite/client-framework/Store/Data/Carts/Handlers/LoadCurrentCart";
import { CartLineCollectionModel, CartLineModel, OrderLineModel } from "@insite/client-framework/Types/ApiModels";

interface ReorderParameter {
    orderLines?: OrderLineModel[],
    qtyValues?: { [key: string]: any },
    onSuccess?: (cartLine: CartLineCollectionModel) => void;
}

type HandlerType = ApiHandlerDiscreteParameter<ReorderParameter, CartLineCollectionModel, CartLineCollectionModel>;

export const DispatchBeginReorder: HandlerType = props => {
    throwErrorIfTesting();

    props.dispatch({
        type: "Pages/OrderDetails/BeginReorder",
    });
};

export const PopulateApiParameter: HandlerType = props => {
    if (!props.parameter.orderLines) {
        throw new Error("Reorder was called but there was no order");
    }

    props.apiParameter = {
        cartLines: props.parameter.orderLines.map(
            // eslint-disable-next-line array-callback-return
            (line: OrderLineModel) => {
                const qtyValue = props.parameter.qtyValues![line.id];
                if (qtyValue && qtyValue !== "0") {
                    return ({
                        productId: line.productId,
                        qtyOrdered: props.parameter.qtyValues![line.id] || null,
                        unitOfMeasure: line.unitOfMeasure,
                    } as CartLineModel);
                }
            }
        ).filter(line => line !== undefined),
    } as CartLineCollectionModel;
};

export const SendDataToApi: HandlerType = async props => {
    props.apiResult = await addLineCollection({
        cartId: API_URL_CURRENT_FRAGMENT,
        cartLineCollection: props.apiParameter,
    });
};

export const DispatchCompleteReorder: HandlerType = props => {
    props.dispatch({
        type: "Pages/OrderDetails/CompleteReorder",
        cartLineCollection: props.apiResult,
    });
};

export const ExecuteOnSuccessCallback: HandlerType = props => {
    props.parameter.onSuccess?.(props.apiResult);
};

export const LoadCart: HandlerType = props => {
    props.dispatch(loadCurrentCart());
};

export const chain = [
    DispatchBeginReorder,
    PopulateApiParameter,
    SendDataToApi,
    DispatchCompleteReorder,
    ExecuteOnSuccessCallback,
    LoadCart,
];

const reorderProduct = createHandlerChainRunnerOptionalParameter(chain, {}, "Reorder Product");
export default reorderProduct;
